import { useState, useEffect } from 'react'
import { request } from '@/lib/request'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import Editor from '@monaco-editor/react'
import { Save, Plus, RefreshCw } from 'lucide-react'

interface Script {
  id: string
  name: string
  code: string
  update_time?: string
}

export default function JsDev() {
  const [scripts, setScripts] = useState<Script[]>([])
  const [current, setCurrent] = useState<Script | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')

  const showMessage = (title: string, description?: string, isError = false) => {
    alert(`${title}${description ? '\n' + description : ''}`)
  }

  const getData = async () => {
    try {
      const resp = await request('/system/api/query/jsScript', {
        page_size: 100,
        page_num: 1,
        query_filter: {},
      })
      if (resp && resp.code === 0) {
        setScripts(resp.data.list || [])
        // 如果当前有选中的脚本，更新它
        if (current) {
          const updated = resp.data.list.find((s: Script) => s.id === current.id)
          if (updated) {
            setCurrent(updated)
            setCode(updated.code)
          }
        }
      }
    } catch (error) {
      showMessage('加载失败', '无法加载脚本列表', true)
    }
  }

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    if (current && current.code) {
      setCode(current.code)
    }
  }, [current])

  const handleSave = async () => {
    if (!current) return

    try {
      const data = {
        ...current,
        code,
        ...(name && { name }),
      }

      const resp = await request('/system/api/update/jsScript', data)
      if (resp && resp.code === 0) {
        showMessage('保存成功', '脚本已更新')
        setName('')
        getData()
      } else {
        showMessage('保存失败', resp?.msg || '更新脚本失败', true)
      }
    } catch (error) {
      showMessage('保存失败', '无法更新脚本', true)
    }
  }

  const handleCreate = async () => {
    if (!name || !code) {
      showMessage('创建失败', '名称和代码不能为空', true)
      return
    }

    try {
      const resp = await request('/system/api/add/jsScript', {
        name,
        code,
      })
      if (resp && resp.code === 0) {
        showMessage('创建成功', '新脚本已添加')
        setName('')
        getData()
      } else {
        showMessage('创建失败', resp?.msg || '添加脚本失败', true)
      }
    } catch (error) {
      showMessage('创建失败', '无法添加脚本', true)
    }
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">JS 开发工具</h1>

      <div className="flex justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 flex-1">
          <SearchableSelect
            className="w-80"
            value={current?.id}
            onChange={(id) => {
              if (id) {
                const script = scripts.find((s) => s.id === id)
                if (script) {
                  setCurrent(script)
                }
              }
            }}
            options={scripts.map((script) => ({
              value: script.id,
              label: script.name,
            }))}
            placeholder="选择脚本"
            searchPlaceholder="搜索脚本..."
            emptyText="未找到脚本"
            allowClear={false}
          />
          <Button variant="outline" size="icon" onClick={getData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Input
            placeholder="新脚本名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-64"
          />
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            新建
          </Button>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-4">
        <Editor
          height="60vh"
          language="javascript"
          value={code}
          onChange={(value) => setCode(value || '')}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            scrollBeyondLastLine: false,
            automaticLayout: true,
          }}
        />

        <div className="flex justify-between items-center">
          {current && (
            <span className="text-sm text-muted-foreground">
              最后更新: {current.update_time || '未知'}
            </span>
          )}
          <Button onClick={handleSave} disabled={!current}>
            <Save className="mr-2 h-4 w-4" />
            保存
          </Button>
        </div>
      </div>
    </div>
  )
}
